import { useCallback } from "react";
import { ethers } from "ethers";
import {
  executePerpsCreateAccount,
  executePerpsMargin,
  executePerpsTrade,
  executePerpsVaultWithdraw,
  type PerpsSponsoredDeployParams,
  type PerpsSponsoredMarginParams,
  type PerpsSponsoredTradeParams,
  type PerpsSponsoredVaultWithdrawParams,
} from "../../operations/perps-operations";
import type { BaseStatusMessage } from "../types";

interface UsePerpsOperationParams {
  chainId: number;
  addLog: (
    message: string,
    type?: "info" | "success" | "error" | "warning",
    txHash?: string,
  ) => void;
  setStatus: (status: BaseStatusMessage | null) => void;
  setLoading: (loading: boolean) => void;
}

interface PerpsOperationResult {
  success: boolean;
  txHash?: string;
  status?: string;
  code?: string;
  recoveryAction?: string;
  recoveryRoute?: string;
  vaultAddress?: string;
  assetToken?: string;
  expectedOutputAmount?: string;
  actualOutputAmount?: string;
  withdrawTxHash?: string;
  depositTxHash?: string;
  outputAmount?: string;
  outputCommitment?: string;
  outputTokenHash?: string;
  leafIndex?: number;
  reshielded?: boolean;
  ownershipCommitment?: string;
  accountNft?: string;
  accountTokenId?: string;
  accountId?: string;
  accountCreateTxHash?: string;
  error?: string;
}

function randomHex32(): string {
  const bytes = new Uint8Array(32);
  globalThis.crypto.getRandomValues(bytes);
  return `0x${Array.from(bytes)
    .map((v) => v.toString(16).padStart(2, "0"))
    .join("")}`;
}

export function usePerpsOperation({
  chainId,
  addLog,
  setStatus,
  setLoading,
}: UsePerpsOperationParams) {
  const createPerpsAccount = useCallback(
    async (
      params: Omit<
        PerpsSponsoredDeployParams,
        "chainId" | "ownershipCommitment"
      > & { ownershipCommitment?: string },
    ): Promise<PerpsOperationResult> => {
      // A random commitment has no opening, and the vault stores it immutably
      // with every exit proof-gated — so the substitution deployed a vault
      // whose contents could never be moved again.
      const ownershipCommitment = params.ownershipCommitment?.trim();
      if (!ownershipCommitment) {
        throw new Error(
          "Refusing to create a perps vault without an ownership commitment: the value is immutable and an unopenable vault permanently strands whatever is funded into it.",
        );
      }
      const deploySalt =
        params.deploySalt?.trim() ||
        ethers.keccak256(
          ethers.toUtf8Bytes(`perps:${Date.now()}:${Math.random()}`),
        );

      setLoading(true);
      setStatus({ type: "info", message: "Creating private perps account..." });
      try {
        const result = await executePerpsCreateAccount({
          chainId,
          vaultOwner: params.vaultOwner,
          accountNft: params.accountNft,
          accountTokenId: params.accountTokenId,
          accountId: params.accountId,
          permissionManager: params.permissionManager,
          ownershipVerifier: params.ownershipVerifier,
          ownershipCommitment,
          autoCreateAccount: params.autoCreateAccount,
          requestedAccountId: params.requestedAccountId,
          perpsMarketProxy: params.perpsMarketProxy,
          deploySalt,
          onLog: addLog,
        });
        if (!result.success) {
          setStatus({
            type: "error",
            message: result.error || "Perps account creation failed",
          });
          return { success: false, error: result.error };
        }
        setStatus({
          type: "success",
          message: "Perps account creation submitted",
        });
        return {
          success: true,
          txHash: result.txHash,
          vaultAddress: result.vaultAddress,
          ownershipCommitment,
          accountNft: result.accountNft,
          accountTokenId: result.accountTokenId,
          accountId: result.accountId,
          accountCreateTxHash: result.accountCreateTxHash,
        };
      } finally {
        setLoading(false);
      }
    },
    [addLog, chainId, setLoading, setStatus],
  );

  const submitPerpsTrade = useCallback(
    async (
      params: Omit<PerpsSponsoredTradeParams, "chainId">,
    ): Promise<PerpsOperationResult> => {
      setLoading(true);
      try {
        const result = await executePerpsTrade({
          chainId,
          ...params,
          onLog: addLog,
        });
        if (!result.success) {
          setStatus({
            type: "error",
            message: result.error || "Perps trade failed",
          });
          return { success: false, error: result.error };
        }
        setStatus({ type: "success", message: "Perps trade submitted" });
        return {
          success: true,
          txHash: result.txHash,
          withdrawTxHash: result.withdrawTxHash,
          depositTxHash: result.depositTxHash,
          outputAmount: result.outputAmount,
          outputCommitment: result.outputCommitment,
          outputTokenHash: result.outputTokenHash,
          leafIndex: result.leafIndex,
          reshielded: result.reshielded,
        };
      } finally {
        setLoading(false);
      }
    },
    [addLog, chainId, setLoading, setStatus],
  );

  const submitPerpsMargin = useCallback(
    async (
      params: Omit<PerpsSponsoredMarginParams, "chainId">,
    ): Promise<PerpsOperationResult> => {
      setLoading(true);
      try {
        const result = await executePerpsMargin({
          chainId,
          ...params,
          onLog: addLog,
        });
        if (!result.success) {
          const isReshieldRequired = result.code === "PERPS_RESHIELD_REQUIRED";
          setStatus({
            type: isReshieldRequired ? "info" : "error",
            message: isReshieldRequired
              ? "Perps withdraw succeeded; private reshield needs recovery"
              : result.error || "Perps margin update failed",
          });
          return {
            success: false,
            error: result.error,
            txHash: result.txHash,
            status: result.status,
            code: result.code,
            recoveryAction: result.recoveryAction,
            recoveryRoute: result.recoveryRoute,
            vaultAddress: result.vaultAddress,
            assetToken: result.assetToken,
            expectedOutputAmount: result.expectedOutputAmount,
            actualOutputAmount: result.actualOutputAmount,
            withdrawTxHash: result.withdrawTxHash,
            depositTxHash: result.depositTxHash,
            outputAmount: result.outputAmount,
            outputCommitment: result.outputCommitment,
            outputTokenHash: result.outputTokenHash,
            leafIndex: result.leafIndex,
            reshielded: result.reshielded,
          };
        }
        setStatus({
          type: "success",
          message: "Perps margin update submitted",
        });
        return {
          success: true,
          txHash: result.txHash,
          status: result.status,
          code: result.code,
          recoveryAction: result.recoveryAction,
          recoveryRoute: result.recoveryRoute,
          vaultAddress: result.vaultAddress,
          assetToken: result.assetToken,
          expectedOutputAmount: result.expectedOutputAmount,
          actualOutputAmount: result.actualOutputAmount,
          withdrawTxHash: result.withdrawTxHash,
          depositTxHash: result.depositTxHash,
          outputAmount: result.outputAmount,
          outputCommitment: result.outputCommitment,
          outputTokenHash: result.outputTokenHash,
          leafIndex: result.leafIndex,
          reshielded: result.reshielded,
        };
      } finally {
        setLoading(false);
      }
    },
    [addLog, chainId, setLoading, setStatus],
  );

  const submitPerpsVaultWithdraw = useCallback(
    async (
      params: Omit<PerpsSponsoredVaultWithdrawParams, "chainId">,
    ): Promise<PerpsOperationResult> => {
      setLoading(true);
      try {
        const result = await executePerpsVaultWithdraw({
          chainId,
          ...params,
          onLog: addLog,
        });
        if (!result.success) {
          setStatus({
            type: "error",
            message: result.error || "Perps vault withdraw failed",
          });
          return { success: false, error: result.error };
        }
        setStatus({
          type: "success",
          message: "Perps vault withdraw submitted",
        });
        return {
          success: true,
          txHash: result.txHash,
          withdrawTxHash: result.withdrawTxHash,
          depositTxHash: result.depositTxHash,
          outputAmount: result.outputAmount,
          outputCommitment: result.outputCommitment,
          outputTokenHash: result.outputTokenHash,
          leafIndex: result.leafIndex,
          reshielded: result.reshielded,
        };
      } finally {
        setLoading(false);
      }
    },
    [addLog, chainId, setLoading, setStatus],
  );

  return {
    createPerpsAccount,
    submitPerpsTrade,
    submitPerpsMargin,
    submitPerpsVaultWithdraw,
  };
}
